/**
 * Personalization Popup
 * Handles multi-page form flow with validation, state management, cart integration, and canvas preview
 */

class PersonalizationPopup {
  constructor() {
    this.state = {
      currentPage: 1,
      engravingStyle: null,
      engravingText: '',
      engravingColor: null,
      agreedToTerms: false,
      confirmedPages: {
        1: false,
        2: false,
        3: false
      }
    };

    this.elements = {
      pages: [],
      prevBtn: null,
      nextBtn: null,
      confirmBtns: [],
      addToCartBtn: null,
      textInput: null,
      charCounter: null,
      styleRadios: [],
      colorRadios: [],
      finalCheckbox: null,
      summaryDisplay: null,
      popupOverlay: null,
      navTitle: null,
      errorContainer: null,
      successContainer: null,
      previewCanvas: null
    };

    this.incompleteMessage = 'Please complete all personalization steps before confirming.';
    this.pageTitles = {};
    this.isLoading = false;
    this.previewRenderer = null;

    this.init();
  }

  init() {
    this.cacheElements();
    this.cachePageTitles();
    this.loadIncompleteMessage();
    this.initPreview();
    this.setupEventListeners();
    this.setupScrollHandler();
    this.setupTouchHandler();
    this.preventPullToRefresh();
    this.showPage(1);
    this.updateButtonStates();
  }

  cacheElements() {
    this.elements.popupOverlay = document.querySelector('.personalization-popup-overlay');
    this.elements.pages = document.querySelectorAll('.personalization-popup-page');
    this.elements.prevBtn = document.querySelector('.personalization-nav-prev');
    this.elements.nextBtn = document.querySelector('.personalization-nav-next');
    this.elements.confirmBtns = document.querySelectorAll('.personalization-confirm-btn');
    this.elements.addToCartBtn = document.querySelector('.personalization-add-to-cart-btn');
    this.elements.textInput = document.getElementById('engraving-text-input');
    this.elements.charCounter = null;
    this.elements.styleRadios = document.querySelectorAll('input[name="engraving-style"]');
    this.elements.colorRadios = document.querySelectorAll('input[name="engraving-color"]');
    this.elements.finalCheckbox = document.getElementById('personalization-final-checkbox');
    this.elements.summaryDisplay = document.querySelector('.personalization-summary-text');
    this.elements.errorContainer = document.querySelector('.personalization-error-message');
    this.elements.successContainer = document.querySelector('.personalization-success-message');
    this.elements.previewCanvas = document.getElementById('personalization-preview-canvas');

  // Validation helper elements
this.elements.helperH1 = document.getElementById('helper-h1');
this.elements.helperH2 = document.getElementById('helper-h2');
this.elements.helperH3 = document.getElementById('helper-h3');
this.elements.helperH3Date = document.getElementById('helper-h3-date');
this.elements.helperH4 = document.getElementById('helper-h4');
this.elements.contactText = document.querySelector('.personalization-contact-text');

  }

  cachePageTitles() {
    const titleContainer = document.getElementById('personalization-page-titles');
    if (titleContainer) {
      this.pageTitles = {
        1: titleContainer.getAttribute('data-page1-title') || 'Choose your engraving style',
        2: titleContainer.getAttribute('data-page2-title') || 'Enter your text',
        3: titleContainer.getAttribute('data-page3-title') || 'Choose your color',
        4: titleContainer.getAttribute('data-page4-title') || 'Review your choices'
      };
    } else {
      this.pageTitles = {
        1: 'Choose your engraving style',
        2: 'Enter your text',
        3: 'Choose your color',
        4: 'Review your choices'
      };
    }

    this.elements.navTitle = document.getElementById('personalization-dynamic-title');
  }

  loadIncompleteMessage() {
    if (this.elements.popupOverlay) {
      const message = this.elements.popupOverlay.getAttribute('data-incomplete-message');
      if (message) {
        this.incompleteMessage = message;
      }
    }
  }

  initPreview() {
    if (!window.PreviewRenderer) {
      console.warn('PreviewRenderer class not found. Preview disabled.');
      this.previewRenderer = null;
      return;
    }

    this.previewRenderer = new window.PreviewRenderer();
    
    if (this.elements.previewCanvas) {
      const initialized = this.previewRenderer.init(this.elements.previewCanvas);
      if (initialized) {
        console.log('Preview renderer initialized successfully');
      } else {
        console.warn('Preview renderer failed to initialize');
        this.previewRenderer = null;
      }
    } else {
      console.warn('Preview canvas element not found');
      this.previewRenderer = null;
    }
  }

  setupEventListeners() {
    if (this.elements.prevBtn) {
      this.elements.prevBtn.addEventListener('click', () => this.previousPage());
    }
    if (this.elements.nextBtn) {
      this.elements.nextBtn.addEventListener('click', () => this.nextPage());
    }

    this.elements.confirmBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => this.handleConfirm(index + 1));
    });

    if (this.elements.addToCartBtn) {
      this.elements.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }

    this.elements.styleRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => this.handleStyleSelection(e));
    });

    if (this.elements.textInput) {
      this.elements.textInput.addEventListener('input', (e) => this.handleTextInput(e));
    }

    this.elements.colorRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => this.handleColorSelection(e));
    });

    if (this.elements.finalCheckbox) {
      this.elements.finalCheckbox.addEventListener('change', (e) => this.handleCheckboxChange(e));
    }
  }

  setupScrollHandler() {
    let scrollTimeout;
    const popupContainer = document.querySelector('.personalization-popup-container');
    
    if (!popupContainer) return;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updatePreview();
      }, 150);
    };

    popupContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    const popupBody = document.querySelector('.personalization-popup-body');
    if (popupBody) {
      popupBody.addEventListener('scroll', handleScroll, { passive: true });
    }
  }

  setupTouchHandler() {
    const popupOverlay = this.elements.popupOverlay;
    
    if (!popupOverlay) return;

    let touchTimeout;
    
    const handleTouchEnd = () => {
      clearTimeout(touchTimeout);
      touchTimeout = setTimeout(() => {
        this.updatePreview();
      }, 100);
    };

    popupOverlay.addEventListener('touchend', handleTouchEnd, { passive: true });
  }

  preventPullToRefresh() {
    const popupOverlay = this.elements.popupOverlay;
    
    if (!popupOverlay) return;

    let startY = 0;
    
    const handleTouchStart = (e) => {
      startY = e.touches[0].pageY;
    };
    
    const handleTouchMove = (e) => {
      const scrollableElement = e.target.closest('.personalization-popup-container, .personalization-popup-body');
      
      if (!scrollableElement) {
        e.preventDefault();
        return;
      }
      
      const currentY = e.touches[0].pageY;
      const scrollTop = scrollableElement.scrollTop;
      
      if (scrollTop <= 0 && currentY > startY) {
        e.preventDefault();
        this.updatePreview();
      }
    };
    
    popupOverlay.addEventListener('touchstart', handleTouchStart, { passive: false });
    popupOverlay.addEventListener('touchmove', handleTouchMove, { passive: false });
  }

  getFontForStyle(styleName) {
  if (!this.previewRenderer || !styleName) return 'Arial';
  
  const settings = this.previewRenderer.settings;
  const style1Name = this.getStyleName(1);
  const style2Name = this.getStyleName(2);
  
  if (styleName === style1Name) {
    return settings.style1Font;
  } else if (styleName === style2Name) {
    return settings.style2Font;
  }
  
  return settings.style1Font;
}


  getStyleName(styleNumber) {
    const cards = document.querySelectorAll('[data-style-name]');
    if (cards[styleNumber - 1]) {
      return cards[styleNumber - 1].dataset.styleName;
    }
    return '';
  }

  getColorHex(colorName) {
    if (!this.previewRenderer || !colorName) return '#B2AFAC';
    
    const settings = this.previewRenderer.settings;
    const color1Name = this.getColorName(1);
    const color2Name = this.getColorName(2);
    const color3Name = this.getColorName(3);
    
    if (colorName === color1Name) {
      return settings.color1Hex;
    } else if (colorName === color2Name) {
      return settings.color2Hex;
    } else if (colorName === color3Name) {
      return settings.color3Hex;
    }
    
    return '#FFFFFF';
  }

  getColorName(colorNumber) {
    const cards = document.querySelectorAll('[data-color-name]');
    if (cards[colorNumber - 1]) {
      return cards[colorNumber - 1].dataset.colorName;
    }
    return '';
  }

  updatePreview() {
    if (!this.previewRenderer) return;
    
    const text = this.state.engravingText;
    const fontFamily = this.getFontForStyle(this.state.engravingStyle);
    const color = this.getColorHex(this.state.engravingColor);
    
    this.previewRenderer.render({
      text: text,
      fontFamily: fontFamily,
      color: color
    });
  }

  clearPreview() {
    if (this.previewRenderer) {
      this.previewRenderer.clear();
    }
  }

  showPage(pageNumber) {
    this.elements.pages.forEach((page) => {
      const pageNum = parseInt(page.getAttribute('data-page'), 10);
      if (pageNum === pageNumber) {
        page.classList.remove('hidden');
        page.style.display = 'block';
      } else {
        page.classList.add('hidden');
        page.style.display = 'none';
      }
    });

    this.state.currentPage = pageNumber;
   // Auto-select and auto-confirm color when only 1 color is active
if (pageNumber === 3) {
  const colorGrid = document.querySelector('.personalization-color-grid');
  const activeColors = colorGrid ? parseInt(colorGrid.getAttribute('data-active-colors'), 10) : 1;
  if (activeColors === 1) {
    const firstColorCard = document.querySelector('[data-color-name]');
    const firstColorRadio = firstColorCard ? firstColorCard.querySelector('input[name="engraving-color"]') : null;
    if (firstColorCard && firstColorRadio && !firstColorRadio.checked) {
      firstColorRadio.checked = true;
      this.state.engravingColor = firstColorCard.getAttribute('data-color-name');
      this.state.confirmedPages[3] = true;
    }
  }
}
 
    if (this.elements.navTitle && this.pageTitles[pageNumber]) {
      this.elements.navTitle.textContent = this.pageTitles[pageNumber];
    }
    
    this.updateNavigationButtons();
    this.updateButtonStates();
    this.updatePreview();
  }

  previousPage() {
    if (this.state.currentPage > 1) {
      this.updatePreview();
      this.showPage(this.state.currentPage - 1);
    }
  }

  nextPage() {
    if (this.state.currentPage < 4) {
      this.updatePreview();
      this.showPage(this.state.currentPage + 1);
    }
  }

  updateNavigationButtons() {
    if (this.elements.prevBtn) {
      this.elements.prevBtn.disabled = this.state.currentPage === 1;
      this.elements.prevBtn.classList.toggle('disabled', this.state.currentPage === 1);
      this.elements.prevBtn.style.visibility = this.state.currentPage === 1 ? 'hidden' : 'visible';
    }

    if (this.elements.nextBtn) {
      const isLastPage = this.state.currentPage === 4;
      const isCurrentPageConfirmed = this.state.confirmedPages[this.state.currentPage];
      
      this.elements.nextBtn.disabled = isLastPage || !isCurrentPageConfirmed;
      this.elements.nextBtn.classList.toggle('disabled', isLastPage || !isCurrentPageConfirmed);
      this.elements.nextBtn.style.visibility = isLastPage ? 'hidden' : 'visible';
    }
  }

  handleStyleSelection(event) {
    const selectedCard = event.target.closest('[data-style-name]');
    if (selectedCard) {
      this.state.engravingStyle = selectedCard.getAttribute('data-style-name');
      this.updatePreview();
      this.updateButtonStates();
    }
  }

  handleTextInput(event) {
  let text = event.target.value;

  if (text.length > 10) {
    text = text.substring(0, 10);
    event.target.value = text;
  }

  this.state.engravingText = text;

  // Mark Page 2 as unconfirmed when text changes
  this.state.confirmedPages[2] = false;

  this.updatePreview();
  this.updateButtonStates();
}


validateEngravingText(text) {
  const errors = [];
  
  if (!text || text.trim() === '') {
    return { valid: false, errors: [] };
  }

  const hasLetters = /[A-Za-z]/.test(text);
  const hasInvalidChars = /[^A-Za-z0-9.]/.test(text);
  const startsWithDot = text.startsWith('.');
  const hasDoubleDots = /\.\./.test(text);
  const dotCount = (text.match(/\./g) || []).length;
  
  if (hasInvalidChars) {
    errors.push('h2');
  }
  
  if (startsWithDot || hasDoubleDots || dotCount > 4) {
    errors.push('h4');
  }

  if (hasLetters) {
    const nonDotChars = text.replace(/\./g, '').length;
    if (nonDotChars > 4) {
      errors.push('h1');
    }
  } else {
    const digits = text.replace(/\./g, '');
    const digitCount = digits.length;
    
    if (digitCount === 0 || digitCount >= 7) {
      errors.push('h3-date');
    } else if (digitCount >= 5 && digitCount <= 6) {
      const isDateLike = this.isDateLikeFormat(text);
      if (!isDateLike) {
        errors.push('h3-date');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors: [...new Set(errors)]
  };
}


isDateLikeFormat(text) {
  if (text.endsWith('.')) {
    return false;
  }

  const parts = text.split('.');
  
  if (parts.length === 2) {
    const left = parts[0].length;
    const right = parts[1].length;
    if ((left === 1 || left === 2) && right === 4) {
      return true;
    }
  }
  
  if (parts.length === 3) {
    const left = parts[0].length;
    const middle = parts[1].length;
    const right = parts[2].length;
    if ((left === 1 || left === 2) && 
        (middle === 1 || middle === 2) && 
        right === 2) {
      return true;
    }
  }
  
  return false;
}

clearValidationErrors() {
  if (this.elements.helperH1) this.elements.helperH1.classList.remove('error');
  if (this.elements.helperH2) this.elements.helperH2.classList.remove('error');
  if (this.elements.helperH3) this.elements.helperH3.classList.remove('error');
  if (this.elements.helperH3Date) this.elements.helperH3Date.classList.remove('error');
  if (this.elements.helperH4) this.elements.helperH4.style.display = 'none';
  if (this.elements.contactText) this.elements.contactText.style.display = 'none';
}


showValidationErrors(errors) {
  this.clearValidationErrors();
  
  if (errors.length === 0) return;
  
  errors.forEach(errorId => {
    if (errorId === 'h3-date') {
      if (this.elements.helperH3Date) {
        this.elements.helperH3Date.classList.add('error');
      }
    } else if (errorId === 'h4') {
      if (this.elements.helperH4) {
        this.elements.helperH4.style.display = 'block';
      }
    } else {
      const element = this.elements[`helper${errorId.toUpperCase()}`];
      if (element) {
        element.classList.add('error');
      }
    }
  });
  
  if (this.elements.contactText) {
    this.elements.contactText.style.display = 'block';
  }
}




  handleColorSelection(event) {
    const selectedCard = event.target.closest('[data-color-name]');
    if (selectedCard) {
      this.state.engravingColor = selectedCard.getAttribute('data-color-name');
      this.updatePreview();
      this.updateButtonStates();
    }
  }

  handleCheckboxChange(event) {
    if (!this.areAllPagesConfirmed()) {
      event.target.checked = false;
      this.state.agreedToTerms = false;
      alert(this.incompleteMessage);
      
      const firstIncompletePage = this.getFirstIncompletePage();
      if (firstIncompletePage) {
        this.showPage(firstIncompletePage);
      }
      
      return;
    }

    this.state.agreedToTerms = event.target.checked;
    this.updateButtonStates();
  }

  areAllPagesConfirmed() {
    return this.state.confirmedPages[1] && 
           this.state.confirmedPages[2] && 
           this.state.confirmedPages[3];
  }

  getFirstIncompletePage() {
    for (let i = 1; i <= 3; i++) {
      if (!this.state.confirmedPages[i]) {
        return i;
      }
    }
    return null;
  }

  updateButtonStates() {
    const currentConfirmBtn = this.getConfirmButtonForPage(this.state.currentPage);

    switch (this.state.currentPage) {
      case 1:
        if (currentConfirmBtn) {
          const isStyleSelected = this.state.engravingStyle !== null;
          currentConfirmBtn.disabled = !isStyleSelected;
          currentConfirmBtn.classList.toggle('disabled', !isStyleSelected);
        }
        break;

      case 2:
  if (currentConfirmBtn) {
    const hasText = this.state.engravingText.length >= 1;
    currentConfirmBtn.disabled = !hasText;
    currentConfirmBtn.classList.toggle('disabled', !hasText);
  }
  break;


      case 3:
        if (currentConfirmBtn) {
          const isColorSelected = this.state.engravingColor !== null;
          currentConfirmBtn.disabled = !isColorSelected;
          currentConfirmBtn.classList.toggle('disabled', !isColorSelected);
        }
        break;

      case 4:
        if (this.elements.addToCartBtn) {
          const isCheckboxChecked = this.state.agreedToTerms;
          this.elements.addToCartBtn.disabled = !isCheckboxChecked || this.isLoading;
          this.elements.addToCartBtn.classList.toggle('disabled', !isCheckboxChecked || this.isLoading);
        }
        this.updateSummary();
        break;

      default:
        break;
    }

    this.updateNavigationButtons();
  }

  getConfirmButtonForPage(pageNumber) {
    const page = document.querySelector(`.personalization-popup-page[data-page="${pageNumber}"]`);
    if (page) {
      return page.querySelector('.personalization-confirm-btn');
    }
    return null;
  }

  handleConfirm(pageNumber) {
  if (pageNumber === 2) {
    const validation = this.validateEngravingText(this.state.engravingText);
    
    if (!validation.valid) {
      this.showValidationErrors(validation.errors);
      return;
    }
    
    this.clearValidationErrors();
  }

  if (this.isCurrentPageValid()) {
    if (pageNumber >= 1 && pageNumber <= 3) {
      this.state.confirmedPages[pageNumber] = true;
    }
    
    this.updatePreview();
    this.nextPage();
  }
}


  isCurrentPageValid() {
    switch (this.state.currentPage) {
      case 1:
        return this.state.engravingStyle !== null;
      case 2:
  if (this.state.engravingText.length === 0) return false;
  const validation = this.validateEngravingText(this.state.engravingText);
  return validation.valid;
      case 3:
        return this.state.engravingColor !== null;
      case 4:
        return this.state.agreedToTerms;
      default:
        return false;
    }
  }

  getVariantId() {
    const selectors = [
      'input[name="id"]',
      'select[name="id"]',
      '[data-variant-id]',
      '[data-product-variant-id]',
      'form[action*="/cart/add"] input[name="id"]',
      '.product-form input[name="id"]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const variantId = element.value || 
                         element.dataset.variantId || 
                         element.dataset.productVariantId;
        if (variantId) {
          console.log('Found variant ID:', variantId);
          return variantId;
        }
      }
    }

    console.warn('Variant ID not found');
    return null;
  }

  buildCartItem(variantId) {
    return {
      id: variantId,
      quantity: 1,
      properties: {
        'Engraving': '',
        'Style': this.state.engravingStyle,
        'Text': this.state.engravingText,
        'Color': this.state.engravingColor
      }
    };
  }

  async handleAddToCart() {
  // Re-validate all pages before adding to cart
  const page2Validation = this.validateEngravingText(this.state.engravingText);
  
  if (!page2Validation.valid) {
    // Show errors on Page 2
    this.showValidationErrors(page2Validation.errors);
    // Navigate back to Page 2
    this.showPage(2);
    return;
  }

  if (!this.isCurrentPageValid()) {
    console.warn('Cannot add to cart: Page 4 validation failed');
    this.showError('Please accept the terms before adding to cart.');
    return;
  }

  if (!this.areAllPagesConfirmed()) {
    this.showError('Please complete all personalization steps.');
    return;
  }

  const variantId = this.getVariantId();
  if (!variantId) {
    this.showError('Unable to find product variant. Please select a variant and try again.');
    return;
  }

  this.setLoadingState(true);
  this.clearErrors();

  try {
    const cartItem = this.buildCartItem(variantId);
    
    console.log('Adding to cart:', cartItem);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(cartItem)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Successfully added to cart:', data);

    this.closePopup();
    this.reset();
    
    window.location.href = '/cart';

  } catch (error) {
    console.error('Error adding to cart:', error);
    this.showError(error.message || 'Failed to add item to cart. Please try again.');
    this.setLoadingState(false);
  }
}


  setLoadingState(isLoading) {
    this.isLoading = isLoading;

    if (this.elements.addToCartBtn) {
      if (isLoading) {
        this.elements.addToCartBtn.disabled = true;
        this.elements.addToCartBtn.dataset.originalText = this.elements.addToCartBtn.textContent;
        this.elements.addToCartBtn.textContent = 'Adding...';
        this.elements.addToCartBtn.classList.add('loading');
      } else {
        this.elements.addToCartBtn.disabled = !this.state.agreedToTerms;
        this.elements.addToCartBtn.textContent = this.elements.addToCartBtn.dataset.originalText || 'Add to Cart';
        this.elements.addToCartBtn.classList.remove('loading');
      }
    }
  }

  showError(message) {
    if (!this.elements.errorContainer) {
      this.elements.errorContainer = document.createElement('div');
      this.elements.errorContainer.className = 'personalization-error-message';
      const page4 = document.querySelector('.personalization-popup-page[data-page="4"]');
      if (page4) {
        page4.insertBefore(this.elements.errorContainer, page4.firstChild);
      }
    }

    this.elements.errorContainer.textContent = message;
    this.elements.errorContainer.style.display = 'block';
    this.elements.errorContainer.setAttribute('role', 'alert');

    setTimeout(() => {
      this.clearErrors();
    }, 5000);
  }

  showSuccess() {
    if (!this.elements.successContainer) {
      this.elements.successContainer = document.createElement('div');
      this.elements.successContainer.className = 'personalization-success-message';
      const page4 = document.querySelector('.personalization-popup-page[data-page="4"]');
      if (page4) {
        page4.insertBefore(this.elements.successContainer, page4.firstChild);
      }
    }

    this.elements.successContainer.textContent = 'Added to cart successfully!';
    this.elements.successContainer.style.display = 'block';
    this.elements.successContainer.setAttribute('role', 'status');

    setTimeout(() => {
      if (this.elements.successContainer) {
        this.elements.successContainer.style.display = 'none';
      }
    }, 3000);
  }

  clearErrors() {
    if (this.elements.errorContainer) {
      this.elements.errorContainer.style.display = 'none';
      this.elements.errorContainer.textContent = '';
    }
  }

  triggerCartUpdate() {
    const cartUpdateEvent = new CustomEvent('cart:updated', {
      detail: { 
        timestamp: new Date(),
        source: 'personalization-popup'
      }
    });
    document.dispatchEvent(cartUpdateEvent);

    if (window.theme && typeof window.theme.openCartDrawer === 'function') {
      window.theme.openCartDrawer();
    } else if (window.CartDrawer && typeof window.CartDrawer.open === 'function') {
      window.CartDrawer.open();
    }

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });
      })
      .catch(err => console.warn('Could not update cart count:', err));
  }

  closePopup() {
    if (this.elements.popupOverlay) {
      this.elements.popupOverlay.style.display = 'none';
      
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.classList.remove('personalization-popup-open');
    }
  }

  updateSummary() {
    if (!this.elements.summaryDisplay) {
      return;
    }

    const summaryText = `
      <strong>Your Personalization:</strong><br>
      Style: ${this.state.engravingStyle || 'Not selected'}<br>
      Text: "${this.state.engravingText || 'No text entered'}"<br>
      Color: ${this.state.engravingColor || 'Not selected'}
    `;

    this.elements.summaryDisplay.innerHTML = summaryText;
  }

  getState() {
    return { ...this.state };
  }

  reset() {
  this.state = {
    currentPage: 1,
    engravingStyle: null,
    engravingText: '',
    engravingColor: null,
    agreedToTerms: false,
    confirmedPages: {
      1: false,
      2: false,
      3: false
    }
  };

  if (this.elements.textInput) {
    this.elements.textInput.value = '';
  }

  // Remove character counter code (no longer exists)

  this.elements.styleRadios.forEach((radio) => {
    radio.checked = false;
  });

  this.elements.colorRadios.forEach((radio) => {
    radio.checked = false;
  });

  if (this.elements.finalCheckbox) {
    this.elements.finalCheckbox.checked = false;
  }

  this.clearErrors();
  if (this.elements.successContainer) {
    this.elements.successContainer.style.display = 'none';
  }

  this.clearValidationErrors();
  this.clearPreview();

  this.showPage(1);
  this.updateButtonStates();
}

}

document.addEventListener('DOMContentLoaded', () => {
  window.personalizationPopup = new PersonalizationPopup();
  console.log('Personalization Popup initialized with real-time preview');
});
